/**
 * useKeyboardShortcuts — global keyboard shortcuts for the comparison
 * workspace (M3 Phase 3.4).
 *
 * Bound keys:
 *
 *     j / n → navigation.next()           (next change — PRD FR-4 spells
 *                                          these as N; j kept for muscle
 *                                          memory)
 *     k / p → navigation.prev()           (previous change — same reasoning)
 *     a  → decisions.decide(id, 'accept') (accept current change)
 *     r  → decisions.decide(id, 'reject') (reject current change)
 *     c  → openAnnotationForChange(id)    (M4 — focus annotation textarea
 *                                          for the active change, opening
 *                                          the inline thread first if needed)
 *     f  → focus.toggle()                 (Phase 3.5; placeholder OK)
 *     v  → view.toggle()                  ('side-by-side' ↔ 'unified')
 *     ?  → onShowCheatsheet()             (open cheatsheet modal)
 *     Esc → onHideCheatsheet() + blur active element
 *
 * Note on `p`: an earlier build bound `p` to "reset to pending". FR-6 makes
 * the pending state reachable by re-clicking the current decision (toggle),
 * so the dedicated shortcut was repurposed to PRD navigation. Removing
 * `p` → pending is intentional; the navigation contract wins.
 *
 * Interception rules (precedence top-down):
 *
 *   1. Skip when an editable element is focused (input / textarea /
 *      contenteditable). Reviewers typing into a future annotation
 *      box must not have `j`/`k`/`n`/`p` accidentally consumed.
 *   2. Skip when a modifier key (Cmd / Ctrl / Alt / Meta) is held —
 *      avoid colliding with browser/OS shortcuts (Cmd+R reload,
 *      Cmd+Shift+P print, etc.).
 *   3. Otherwise dispatch to the matching handler and `preventDefault()`
 *      so the browser doesn't double-handle (e.g. quick-find type-ahead).
 *
 * `?` deserves a special note: most layouts emit `?` only when Shift is
 * held. We therefore allow `Shift` as the *one* modifier exception —
 * Shift on its own is fine; Cmd / Ctrl / Alt still suppress.
 */
import { useEffect, useRef } from 'react';

import type { DecisionAdapter } from './decisionAdapter';
import type { UseChangeNavigationResult } from './useChangeNavigation';

export interface FocusModeAdapter {
  /** Toggle focus reading mode. Phase 3.5 wires this; Phase 3.4 may pass a noop. */
  toggle: () => void;
}

export interface ViewModeAdapter {
  /** Flip between side-by-side and unified views. */
  toggle: () => void;
}

interface UseKeyboardShortcutsOptions {
  navigation: UseChangeNavigationResult;
  decisions: DecisionAdapter | undefined;
  view: ViewModeAdapter;
  focus: FocusModeAdapter;
  /** Open the `?` cheatsheet modal. */
  onShowCheatsheet: () => void;
  /** Close the cheatsheet modal (also fires on `Esc`). */
  onHideCheatsheet: () => void;
  /** Master kill-switch — pass false to disable (e.g. while a confirm dialog is open). */
  enabled?: boolean;
}

/** Tags whose default behaviour is text editing — we never steal keys from them. */
const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Open (or focus) the inline annotation thread for the given change.
 * Looks the thread up by `[data-change-id]` so this hook stays decoupled
 * from the AnnotationThread component — we only depend on a stable DOM
 * contract. If the textarea is already mounted we just focus it; otherwise
 * we click the toggle to expand the thread, then focus the textarea on the
 * next paint (after React renders the form).
 */
function openAnnotationForChange(changeId: string): void {
  const thread = document.querySelector<HTMLElement>(
    `[data-testid="annotation-thread"][data-change-id="${changeId}"]`,
  );
  if (!thread) return;

  thread.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const existingTextarea = thread.querySelector<HTMLTextAreaElement>('textarea');
  if (existingTextarea) {
    existingTextarea.focus();
    return;
  }

  const toggle = thread.querySelector<HTMLButtonElement>(
    '[data-testid="annotation-toggle"]',
  );
  if (!toggle || toggle.disabled) return;
  toggle.click();

  // Wait one paint so React commits the open state and renders the form,
  // then focus the new textarea. requestAnimationFrame is enough because
  // the thread's `open` flag is local state (synchronous setState + render).
  requestAnimationFrame(() => {
    const textarea = thread.querySelector<HTMLTextAreaElement>('textarea');
    textarea?.focus();
  });
}

export function useKeyboardShortcuts({
  navigation,
  decisions,
  view,
  focus,
  onShowCheatsheet,
  onHideCheatsheet,
  enabled = true,
}: UseKeyboardShortcutsOptions): void {
  // Ref-mirror every callback so the listener body doesn't have to
  // be re-attached on every parent re-render. The DOM listener is
  // attached once per `enabled` flip.
  const handlersRef = useRef({
    navigation,
    decisions,
    view,
    focus,
    onShowCheatsheet,
    onHideCheatsheet,
  });

  useEffect(() => {
    handlersRef.current = {
      navigation,
      decisions,
      view,
      focus,
      onShowCheatsheet,
      onHideCheatsheet,
    };
  }, [navigation, decisions, view, focus, onShowCheatsheet, onHideCheatsheet]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof document === 'undefined') return;

    const listener = (event: KeyboardEvent): void => {
      // Rule 1: never steal from text editors.
      if (isEditableTarget(event.target)) return;

      // Rule 2: skip combos with Cmd / Ctrl / Alt / Meta. (Shift is OK
      // because `?` requires it on most layouts; `J` and `K` already
      // share keys with their lowercase variants and we read `event.key`
      // case-insensitively below.)
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;

      // Esc — always handled, regardless of `?`-state.
      if (key === 'Escape') {
        handlersRef.current.onHideCheatsheet();
        if (
          document.activeElement instanceof HTMLElement &&
          document.activeElement !== document.body
        ) {
          document.activeElement.blur();
        }
        // Don't preventDefault — let other components (modals) react too.
        return;
      }

      // `?` and Shift+/ both fire `event.key === '?'` on standard
      // layouts. We accept either.
      if (key === '?') {
        handlersRef.current.onShowCheatsheet();
        event.preventDefault();
        return;
      }

      // Single-letter shortcuts — case-insensitive.
      const lower = key.length === 1 ? key.toLowerCase() : key;
      switch (lower) {
        // PRD FR-4 spells these as N / P; we keep j / k for backwards-
        // compatibility muscle memory. Both fall through to the same
        // handler — see the file header for the rationale.
        case 'j':
        case 'n':
          handlersRef.current.navigation.next();
          event.preventDefault();
          return;
        case 'k':
        case 'p':
          handlersRef.current.navigation.prev();
          event.preventDefault();
          return;
        case 'a': {
          const dec = handlersRef.current.decisions;
          const id = handlersRef.current.navigation.selectedChangeId;
          if (dec && dec.canDecide && id) {
            dec.decide(id, 'accept');
            event.preventDefault();
          }
          return;
        }
        case 'r': {
          const dec = handlersRef.current.decisions;
          const id = handlersRef.current.navigation.selectedChangeId;
          if (dec && dec.canDecide && id) {
            dec.decide(id, 'reject');
            event.preventDefault();
          }
          return;
        }
        case 'z': {
          // Reset current change to pending. Replaces the legacy `p` →
          // pending shortcut (repurposed for PRD FR-4 navigation). Vim
          // style: `z` for "zap" / undo. Without this, keyboard users
          // had no way back to pending after accept/reject — code review
          // HIGH finding.
          const dec = handlersRef.current.decisions;
          const id = handlersRef.current.navigation.selectedChangeId;
          if (dec && dec.canDecide && id) {
            dec.decide(id, 'pending');
            event.preventDefault();
          }
          return;
        }
        case 'c': {
          const id = handlersRef.current.navigation.selectedChangeId;
          if (!id) return;
          openAnnotationForChange(id);
          event.preventDefault();
          return;
        }
        case 'f':
          handlersRef.current.focus.toggle();
          event.preventDefault();
          return;
        case 'v':
          handlersRef.current.view.toggle();
          event.preventDefault();
          return;
        default:
          return;
      }
    };

    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [enabled]);
}
